import React from 'react';
import { GECKO_CONSTS, GeckoFields, GeckoIcon, GeckoToastService } from 'ui';
import './index.scss';
import {
  convertOptionsAndDefaultsToValueMap,
  getDefaultAsIdFromSingleEntryOptions,
  isMultiSelectField,
  newOption,
  setDefaultValueForType,
  splitKeyIntoIndexAndKeyName,
  addNewOptionIfAllAreArchived,
  archiveOption,
  isArchivedOption,
  optionHasValue,
} from './logic';

const OPTION_WIDTH = '42%';
const HEADER_LABELS = [
  {
    label: 'Label',
    width: OPTION_WIDTH,
    key: 'label_title',
    type: GECKO_CONSTS.FIELD_TYPE_DISPLAY,
  },
  {
    label: 'Value',
    width: OPTION_WIDTH,
    key: 'value_title',
    type: GECKO_CONSTS.FIELD_TYPE_DISPLAY,
  },
];

const SingleEntryOptionsList = ({
  options: propOptions,
  updateState: updateParentState,
  defaultValues: propDefaultValues,
  type: selectFieldType,
  swapModeButton,
  usedValues = [],
}) => {
  const defaultValues = setDefaultValueForType(
    propDefaultValues,
    selectFieldType
  );

  const options =
    propOptions && propOptions.length > 0 ? [...propOptions] : [newOption()];

  const addOptionRowAt = (index) => {
    options.splice(index + 1, 0, newOption());
    updateParentState(options, defaultValues);
  };

  const removeDeletedOptionsFromDefaultValues = ({ id }, defaultValues) => {
    if (isMultiSelectField(selectFieldType)) {
      return defaultValues.filter((defaultValueId) => defaultValueId !== id);
    }
    return defaultValues === id ? null : defaultValues;
  };

  const getAndArchiveOption = (option) => {
    // Modify state explicitly here as its the simplest way to track deletion
    archiveOption(option);
    return option;
  };

  const canDeleteOption = (option) =>
    !(usedValues[option?.value] || usedValues[option?.id]);

  const getDeletionError = (option) => {
    const workflowsUsedByOption =
      usedValues[option.value] || usedValues[option.id];
    return workflowsUsedByOption.length === 1
      ? `Unable to delete option: Option is used in workflow "${workflowsUsedByOption}".`
      : `Unable to delete option: Option is used in workflows "${workflowsUsedByOption.join(
          '", "'
        )}".`;
  };

  const deleteOptionRowAt = (index) => {
    const option = options[index];
    if (!canDeleteOption(option)) {
      return new GeckoToastService().error(getDeletionError(option));
    }

    const isNewOption = option?.isNew;
    const [deletedValue] = isNewOption
      ? options.splice(index, 1)
      : [getAndArchiveOption(option)];

    const updatedDefaultValue = removeDeletedOptionsFromDefaultValues(
      deletedValue,
      defaultValues
    );
    const updatedOptions = addNewOptionIfAllAreArchived(options);

    updateParentState(updatedOptions, updatedDefaultValue);
  };

  const handleChange = (key, value) => {
    if (key === 'default_value') {
      const newDefaultValues = getDefaultAsIdFromSingleEntryOptions(
        value,
        options,
        selectFieldType
      );
      return updateParentState(options, newDefaultValues);
    }

    const [keyName, index] = splitKeyIntoIndexAndKeyName(key);
    const newOptions = [...options];
    newOptions[index][keyName] = value;
    return updateParentState(newOptions, defaultValues);
  };

  const renderOptionInput = (option, index) => {
    if (isArchivedOption(option)) {
      return [];
    }
    return [
      {
        key: `label-${index}`,
        width: OPTION_WIDTH,
        type: GECKO_CONSTS.FIELD_TYPE_TEXT,
        onBlur: () =>
          !options[index]?.value
            ? handleChange(`value-${index}`, options[index]?.label)
            : undefined,
      },
      {
        key: `value-${index}`,
        width: OPTION_WIDTH,
        type: GECKO_CONSTS.FIELD_TYPE_TEXT,
        disabled: !options[index]?.isNew,
      },
      {
        key: 'modify_list',
        width: '12%',
        type: GECKO_CONSTS.FIELD_TYPE_DISPLAY,
        render: () => {
          return (
            <>
              <button type="button" onClick={() => addOptionRowAt(index)}>
                <GeckoIcon icon="far fa-plus" />
              </button>
              <button type="button" onClick={() => deleteOptionRowAt(index)}>
                <GeckoIcon icon="far fa-trash-alt" />
              </button>
            </>
          );
        },
      },
    ];
  };

  const renderDefaultValueInput = () => {
    return [
      {
        label: 'Default Value',
        key: 'default_value',
        description:
          'If set any new Contacts that are created without this field filled in will be given this default value',
        type: selectFieldType,
        options: options.filter(
          (option) => optionHasValue(option) && !isArchivedOption(option)
        ),
        width: '100%',
      },
    ];
  };

  const renderFields = () => [
    ...HEADER_LABELS,
    ...options.flatMap(renderOptionInput),
    swapModeButton,
    ...renderDefaultValueInput(),
  ];
  return (
    <div className="SingleEntryOptionsList">
      <GeckoFields
        handler={handleChange}
        values={convertOptionsAndDefaultsToValueMap(
          options,
          defaultValues,
          selectFieldType
        )}
        fields={renderFields()}
      />
    </div>
  );
};

export default SingleEntryOptionsList;
